import React, { useState } from 'react';
import Slider from 'react-slick';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Gallery.css';

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000'; // Базовый URL сервера

// Функция для правильной обработки URL изображений
const getImageUrl = (image) => {
  if (!image) return null;
  if (typeof image === 'string') {
    const trimmed = image.trim();
    if (!trimmed) return null;
    
    // Если уже полный URL (S3 или другой)
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    // Если относительный путь
    if (trimmed.startsWith('/')) {
      return `${BASE_URL}${trimmed}`;
    }
    // Если путь без слеша в начале
    return `${BASE_URL}/${trimmed}`;
  }
  return null;
};

const Gallery = ({ images }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: (index) => setCurrentIndex(index),
    arrows: false,
  };

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  if (!images || images.length === 0) {
    return <p>Нет доступных изображений</p>;
  }

  // Нормализуем URL изображений и убираем дубликаты
  const seenUrls = new Set();
  const normalizedImages = [];
  
  // Сначала нормализуем все URL
  const allUrls = images
    .map(image => getImageUrl(image))
    .filter(url => url !== null);
  
  // Затем убираем дубликаты, сохраняя порядок
  for (const url of allUrls) {
    if (url && !seenUrls.has(url)) {
      seenUrls.add(url);
      normalizedImages.push(url);
    }
  }

  if (normalizedImages.length === 0) {
    return <p>Нет доступных изображений</p>;
  }

  const lightboxSlides = normalizedImages.map((imageUrl) => ({
    src: imageUrl,
  }));

  // Если только одно изображение, показываем его просто, без галереи
  if (normalizedImages.length === 1) {
    return (
      <>
        <div className="desktop-gallery-single">
          <div className="gallery-main">
            <img
              src={normalizedImages[0]}
              alt="Main"
              onClick={() => openLightbox(0)}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/600';
              }}
            />
          </div>
        </div>
        <div className="mobile-gallery-single">
          <img
            src={normalizedImages[0]}
            alt="Main"
            onClick={() => openLightbox(0)}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/600';
            }}
          />
        </div>
        {isOpen && (
          <Lightbox
            slides={lightboxSlides}
            open={isOpen}
            index={photoIndex}
            close={() => setIsOpen(false)}
            onIndexChange={setPhotoIndex}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* Галерея для десктопа */}
      <div className="desktop-gallery">
        <div className="gallery-main">
          <img
            src={normalizedImages[0]}
            alt="Main"
            onClick={() => openLightbox(0)}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/600';
            }}
          />
        </div>
        <div className="gallery-thumbnails">
          {normalizedImages.slice(1, 5).map((imageUrl, index) => (
            <div
              key={`thumb-${index + 1}-${imageUrl}`}
              className="thumbnail"
              onClick={() => openLightbox(index + 1)}
            >
              <img 
                src={imageUrl} 
                alt={`Thumbnail ${index + 1}`}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/150';
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Галерея для мобильных устройств */}
      <div className="mobile-gallery">
        <Slider {...sliderSettings}>
          {normalizedImages.map((imageUrl, index) => (
            <div key={`mobile-${index}-${imageUrl}`} className="slider-image">
              <img 
                src={imageUrl} 
                alt={`Фото ${index + 1}`}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/600';
                }}
              />
            </div>
          ))}
        </Slider>
        <div className="image-indicator">
          {currentIndex + 1} из {normalizedImages.length}
        </div>
      </div>

      {/* Lightbox для полноэкранного просмотра */}
      {isOpen && (
        <Lightbox
          slides={lightboxSlides}
          open={isOpen}
          index={photoIndex}
          close={() => setIsOpen(false)}
          onIndexChange={setPhotoIndex}
        />
      )}
    </>
  );
};

export default Gallery;
